import { FC } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { AboutContent } from '../services/contentService';
import firmImage from '../assets/zdjęciefirmowe.jpeg';

interface AboutUsProps {
  content?: AboutContent;
}

const AnimatedStat: FC<{ value: number; suffix: string; label: string }> = ({ value, suffix, label }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <motion.div 
      ref={ref}
      className="p-4 bg-gray-50 rounded-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.7 }}
    >
      <div className="font-bold text-2xl text-blue-700">
        {inView ? (
          <CountUp end={value} duration={2.5} suffix={suffix} />
        ) : (
          `0${suffix}`
        )}
      </div>
      <div className="text-gray-700">{label}</div>
    </motion.div>
  );
};

const AboutUs: FC<AboutUsProps> = ({ content }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Default content if CMS data is not available
  const defaultContent: AboutContent = {
    title: "O Firmie Aluxim",
    content: "Aluxim to firma z wieloletnim doświadczeniem w branży budowlanej, specjalizująca się w nowoczesnych rozwiązaniach architektonicznych. Nasze projekty łączą funkcjonalność, estetykę i najwyższą jakość wykonania. Współpracujemy z renomowanymi dostawcami materiałów, co pozwala nam oferować produkty najwyższej jakości."
  };

  const aboutContent = content || defaultContent;
  const currentYear = new Date().getFullYear();
  const yearsInBusiness = currentYear - 2007;

  return (
    <section id="about" className="py-16 bg-white">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row items-center">
          <motion.div 
            className="lg:w-1/2 mb-10 lg:mb-0"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
            ref={ref}
          >
            <div className="bg-gray-200 rounded-lg h-96 w-full overflow-hidden">
              <motion.img
                src={aboutContent.image || firmImage}
                alt="Firma Aluxim"
                className="w-full h-full object-cover"
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.2 }}
              />
            </div>
          </motion.div>
          <div className="lg:w-1/2 lg:pl-12">
            <motion.h2 
              className="text-3xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              {aboutContent.title}
            </motion.h2>
            <motion.div 
              className="text-gray-700 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {aboutContent.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </motion.div>
            <div className="grid grid-cols-2 gap-4">
              <AnimatedStat value={yearsInBusiness} suffix="+" label="Lat doświadczenia" />
              <AnimatedStat value={1000} suffix="+" label="Realizacji" />
              <AnimatedStat value={100} suffix="%" label="Zadowolonych klientów" />
              <AnimatedStat value={2} suffix=" lat" label="Gwarancji" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
